import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import consultationVaccinationService from '../../services/consultationVaccinationService';
import vaccineCatalogService from '../../services/vaccineCatalogService';
import Icon from '../common/Icons';
import { ClinicalButton, ClinicalButtonStack } from './ui';
import './ConsultationVaccinationsPanel.css';

import {
  formatAppDateTime,
  nowDateTimeLocalValue,
  toDateTimeLocalValue,
  fromDateTimeLocalValue
} from '../../utils/appTimezone';

const DOSE_OPTIONS = [
  { value: '', label: 'Non précisé' },
  { value: '1', label: '1ère dose' },
  { value: '2', label: '2ème dose' },
  { value: '3', label: '3ème dose' },
  { value: '0', label: 'Rappel' }
];

const emptyForm = () => ({
  catalog_vaccine_id: '',
  administered_at: nowDateTimeLocalValue(),
  dose_number: '',
  batch_number: '',
  notes: ''
});

function formatAdministeredAt(value) {
  return formatAppDateTime(value, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function ConsultationVaccinationsPanel({ consultation, embedded = false, onSuccess }) {
  const cid = consultation?.id;
  const [catalog, setCatalog] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const loadData = useCallback(async () => {
    if (!cid) return;
    setLoading(true);
    setError('');
    try {
      const [catalogRes, listRes] = await Promise.all([
        vaccineCatalogService.list({ is_active: 'true' }),
        consultationVaccinationService.list(cid)
      ]);
      if (catalogRes.success) {
        setCatalog(Array.isArray(catalogRes.data) ? catalogRes.data : []);
      }
      if (listRes.success) {
        setRecords(Array.isArray(listRes.data) ? listRes.data : []);
      }
    } catch (e) {
      setError(e.message || 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [cid]);

  useEffect(() => {
    if (!cid) return;
    setRecords([]);
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
    loadData();
  }, [cid, loadData]);

  const resetFormState = () => {
    setForm(emptyForm());
    setEditingId(null);
    setShowForm(false);
  };

  const openCreate = () => {
    setForm(emptyForm());
    setEditingId(null);
    setShowForm(true);
    setError('');
  };

  const openEdit = (record) => {
    setEditingId(record.id);
    setForm({
      catalog_vaccine_id: String(record.catalog_vaccine_id),
      administered_at: record.administered_at
        ? toDateTimeLocalValue(record.administered_at)
        : emptyForm().administered_at,
      dose_number: record.dose_number != null ? String(record.dose_number) : '',
      batch_number: record.batch_number || '',
      notes: record.notes || ''
    });
    setShowForm(true);
    setError('');
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const setNow = () => {
    handleChange('administered_at', nowDateTimeLocalValue());
  };

  const buildPayload = () => ({
    catalog_vaccine_id: parseInt(form.catalog_vaccine_id, 10),
    administered_at: fromDateTimeLocalValue(form.administered_at) || form.administered_at,
    dose_number: form.dose_number !== '' ? parseInt(form.dose_number, 10) : null,
    batch_number: form.batch_number.trim() || null,
    notes: form.notes.trim() || null
  });

  const handleSave = async () => {
    if (!cid) return;
    if (!form.catalog_vaccine_id) {
      setError('Sélectionnez un vaccin du catalogue.');
      return;
    }
    if (!form.administered_at) {
      setError('Date et heure d\'administration requises.');
      return;
    }

    setBusy(true);
    setError('');
    try {
      const payload = buildPayload();
      const res = editingId
        ? await consultationVaccinationService.update(cid, editingId, payload)
        : await consultationVaccinationService.add(cid, payload);
      if (!res.success) throw new Error(res.message || 'Enregistrement refusé.');
      resetFormState();
      await loadData();
      onSuccess?.();
    } catch (e) {
      setError(e.message || 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (record) => {
    if (!cid || !window.confirm(`Supprimer la vaccination « ${record.vaccine_name} » ?`)) return;
    setBusy(true);
    setError('');
    try {
      const res = await consultationVaccinationService.remove(cid, record.id);
      if (!res.success) throw new Error(res.message || 'Suppression refusée.');
      if (editingId === record.id) resetFormState();
      await loadData();
      onSuccess?.();
    } catch (e) {
      setError(e.message || 'Suppression impossible.');
    } finally {
      setBusy(false);
    }
  };

  if (!cid) return null;

  return (
    <div className={`vaccinations-panel${embedded ? ' vaccinations-panel--embedded' : ''}`}>
      {loading ? (
        <div className="vaccinations-panel__loading" role="status" aria-live="polite">
          <Spinner animation="border" size="sm" />
          <span>Chargement des vaccinations…</span>
        </div>
      ) : null}

      {error ? (
        <Alert variant="danger" className="vaccinations-panel__alert py-2 small mb-2">
          {error}
        </Alert>
      ) : null}

      {!loading && records.length > 0 ? (
        <ul className="vaccinations-panel__list" aria-label="Vaccinations administrées">
          {records.map((record) => (
            <li key={record.id} className="vaccinations-panel__card">
              <div className="vaccinations-panel__card-icon" aria-hidden>
                <Icon name="shield" size={18} />
              </div>
              <div className="vaccinations-panel__card-body">
                <div className="vaccinations-panel__card-head">
                  <strong className="vaccinations-panel__card-title">{record.vaccine_name}</strong>
                  {record.dose_label ? (
                    <span className="vaccinations-panel__dose-badge">{record.dose_label}</span>
                  ) : null}
                </div>
                  <p className="vaccinations-panel__card-meta">
                    {[
                      formatAdministeredAt(record.administered_at),
                      record.batch_number ? `Lot ${record.batch_number}` : null
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                  {record.notes?.trim() ? (
                    <p className="vaccinations-panel__card-notes">{record.notes.trim()}</p>
                  ) : null}
                </div>
                <div className="vaccinations-panel__card-actions">
                  <button
                    type="button"
                    className="vaccinations-panel__icon-btn"
                    onClick={() => openEdit(record)}
                    disabled={busy}
                    aria-label={`Modifier ${record.vaccine_name}`}
                    title="Modifier"
                  >
                    <Icon name="edit" size={15} />
                  </button>
                  <button
                    type="button"
                    className="vaccinations-panel__icon-btn vaccinations-panel__icon-btn--danger"
                    onClick={() => handleRemove(record)}
                    disabled={busy}
                    aria-label={`Supprimer ${record.vaccine_name}`}
                    title="Supprimer"
                  >
                    <Icon name="trash" size={15} />
                  </button>
                </div>
              </li>
          ))}
        </ul>
      ) : null}

      {!loading && records.length === 0 && !showForm ? (
        <div className="vaccinations-panel__empty">
          <span className="vaccinations-panel__empty-icon" aria-hidden>
            <Icon name="shield" size={22} />
          </span>
          <p className="mb-0">Aucune vaccination enregistrée pour cette consultation.</p>
        </div>
      ) : null}

      {showForm ? (
        <div className="vaccinations-panel__form">
          <div className="vaccinations-panel__form-head">
            <h3 className="vaccinations-panel__form-title">
              {editingId ? 'Modifier la vaccination' : 'Nouvelle vaccination'}
            </h3>
            <p className="vaccinations-panel__form-hint mb-0">
              Sélectionnez le vaccin du référentiel SMIE et renseignez le lot administré.
            </p>
          </div>

          <div className="vaccinations-panel__field">
            <label className="vaccinations-panel__label" htmlFor="cv-vaccine">
              Vaccin *
            </label>
            <select
              id="cv-vaccine"
              className="vaccinations-panel__input vaccinations-panel__select"
              value={form.catalog_vaccine_id}
              onChange={(e) => handleChange('catalog_vaccine_id', e.target.value)}
            >
              <option value="">— Choisir un vaccin —</option>
              {catalog.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>

          <div className="vaccinations-panel__field-row">
            <div className="vaccinations-panel__field vaccinations-panel__field--grow">
              <label className="vaccinations-panel__label" htmlFor="cv-datetime">
                Date & heure *
              </label>
              <div className="vaccinations-panel__datetime-wrap">
                <input
                  id="cv-datetime"
                  type="datetime-local"
                  className="vaccinations-panel__input"
                  value={form.administered_at}
                  onChange={(e) => handleChange('administered_at', e.target.value)}
                />
                <button
                  type="button"
                  className="vaccinations-panel__now-btn"
                  onClick={setNow}
                  disabled={busy}
                >
                  Maintenant
                </button>
              </div>
            </div>
            <div className="vaccinations-panel__field">
              <label className="vaccinations-panel__label" htmlFor="cv-dose">
                Dose
              </label>
              <select
                id="cv-dose"
                className="vaccinations-panel__input vaccinations-panel__select"
                value={form.dose_number}
                onChange={(e) => handleChange('dose_number', e.target.value)}
              >
                {DOSE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="vaccinations-panel__field">
            <label className="vaccinations-panel__label" htmlFor="cv-lot">
              N° de lot
            </label>
            <input
              id="cv-lot"
              type="text"
              className="vaccinations-panel__input"
              value={form.batch_number}
              onChange={(e) => handleChange('batch_number', e.target.value)}
              maxLength={64}
              placeholder="Ex. AB1234"
            />
          </div>

          <div className="vaccinations-panel__field">
            <label className="vaccinations-panel__label" htmlFor="cv-notes">
              Notes cliniques
            </label>
            <textarea
              id="cv-notes"
              className="vaccinations-panel__textarea"
              rows={2}
              value={form.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Réactions, observations, contre-indications écartées…"
            />
          </div>

          <ClinicalButtonStack className="vaccinations-panel__form-actions">
            <ClinicalButton
              type="button"
              variant="primary"
              size="sm"
              disabled={busy}
              onClick={handleSave}
            >
              {busy ? 'Enregistrement…' : editingId ? 'Mettre à jour' : 'Enregistrer'}
            </ClinicalButton>
            <ClinicalButton
              type="button"
              variant="outline-secondary"
              size="sm"
              disabled={busy}
              onClick={resetFormState}
            >
              Annuler
            </ClinicalButton>
          </ClinicalButtonStack>
        </div>
      ) : (
        !loading && (
          <button type="button" className="vaccinations-panel__add-btn" onClick={openCreate}>
            <Icon name="plus" size={15} aria-hidden />
            Ajouter une vaccination
          </button>
        )
      )}
    </div>
  );
}
